import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// iOS Safari-specific navigator extension
interface NavigatorStandalone extends Navigator {
  standalone?: boolean;
}

// Detect iOS device
const isIOS = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

// Detect if running in Safari (not Chrome/Firefox on iOS)
const isInSafari = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/firefox/.test(userAgent);
};

// Detect if already running as standalone PWA
const isInStandaloneMode = (): boolean => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as NavigatorStandalone).standalone === true
  );
};

export const usePwaInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  useEffect(() => {
    // Check if already installed as standalone
    if (isInStandaloneMode()) {
      setIsAppInstalled(true);
      return;
    }

    // Check if iOS device
    const iosDevice = isIOS();
    setIsIosDevice(iosDevice);

    // On iOS Safari, show install option (manual instructions)
    if (iosDevice && isInSafari()) {
      setIsInstallable(true);
      return;
    }

    // For Chrome/Android/Desktop, listen for the beforeinstallprompt event
    // This event fires when Chrome determines the app is installable
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setShowInstallModal(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Show the install modal (for both iOS and Android)
  const showInstallPrompt = useCallback(() => {
    setShowInstallModal(true);
  }, []);

  // Dismiss the install modal
  const dismissInstallModal = useCallback(() => {
    setShowInstallModal(false);
  }, []);

  // Trigger the actual PWA installation (Android only)
  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) {
      console.warn('Install prompt not available');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallable(false);
      }
      setShowInstallModal(false);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  }, [deferredPrompt]);

  return {
    isInstallable,
    isAppInstalled,
    isIosDevice,
    showInstallModal,
    showInstallPrompt,
    dismissInstallModal,
    triggerInstall,
  };
};
